import React, {
  useState, useEffect, useContext,
} from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PropTypes from 'prop-types';
import { sendRequest } from '../../api/shootAPI';
import API from '../../api/endpoints';
import { NotificationContext } from '../../contexts/Notification';
import { UserContext } from '../../contexts/User';
import { MemberSelector } from '../../components/selectors/MemberNameSelector';
import { TeamSelector } from '../../components/selectors/TeamSelector';
// eslint-disable-next-line camelcase
import error_outline from '../../images/error_outline.svg';

function AddProjectModal({
  open, onClose, onSubmit, loading = false, title = 'New Project', initialData = {},
}) {
  const [activeTab, setActiveTab] = useState(0);

  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const { displayNotification } = useContext(NotificationContext);
  const { activeWorkspace } = useContext(UserContext);
  const [workspaceMembers, setWorkspaceMembers] = useState([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);

  const [workspaceTeams, setWorkspaceTeams] = useState([]);
  const [isLoadingTeams, setIsLoadingTeams] = useState(false);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const [formData, setFormData] = useState({
    name: initialData.name || '',
    description: initialData.description || '',
    billable: initialData.billable || true,
    disableActivity: initialData.disableActivity || false,
    disableIdleTime: initialData.disableIdleTime || false,
    client: initialData.client || '',
    managers: initialData.managers || [],
    members: initialData.members || [],
    viewers: initialData.viewers || [],
    teams: initialData.teams || [],
  });

  const handleInputChange = (field) => (event) => {
    const { value, checked, type } = event.target;
    setFormData((prev) => ({
      ...prev,
      [field]: type === 'checkbox' ? checked : value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const fetchMembers = async () => {
    setIsLoadingMembers(true);
    try {
      const { success, data } = await sendRequest({
        method: 'get',
        url: API.WORKSPACE.members(activeWorkspace.id),
        params: {
          pageSize: 0,
        },
      });

      if (success) {
        const filteredMembers = data.members
          .filter(({ haveJoined }) => haveJoined)
          .map((item) => ({
            id: item.id,
            name: `${item.user.firstName} ${item.user.lastName}`,
          }));
        filteredMembers.sort((item1, item2) => {
          if (item1.name > item2.name) return 1;
          if (item1.name < item2.name) return -1;
          return 0;
        });
        setWorkspaceMembers(filteredMembers);
      } else {
        displayNotification({
          message: 'There was an issue fetching members in this workspace. Please try again',
          severity: 'error',
        });
      }
    } catch (error) {
      displayNotification({
        message: 'Error fetching workspace members',
        severity: 'error',
      });
    }
    setIsLoadingMembers(false);
  };

  const fetchTeams = async () => {
    setIsLoadingTeams(true);
    try {
      const { success, data } = await sendRequest({
        method: 'get',
        url: API.WORKSPACE.getWorkspaceTeams(activeWorkspace.id),
        params: {
          pageSize: 0,
        },
      });

      if (success) {
        const filteredTeams = data.formattedTeams
          .map((item) => ({
            id: item.id,
            name: item.name,
          }));
        filteredTeams.sort((item1, item2) => {
          if (item1.name > item2.name) return 1;
          if (item1.name < item2.name) return -1;
          return 0;
        });
        setWorkspaceTeams(filteredTeams);
      } else {
        displayNotification({
          message: 'There was an issue fetching teams in this workspace. Please try again',
          severity: 'error',
        });
      }
    } catch (error) {
      displayNotification({
        message: 'Error fetching workspace teams',
        severity: 'error',
      });
    }
    setIsLoadingTeams(false);
  };

  const handleSelectAllTeams = () => {
    setFormData({
      ...formData,
      teams: [...workspaceTeams],
    });
  };

  const handleSelectAllManagers = () => {
    setFormData({
      ...formData,
      managers: workspaceMembers.filter((member) => !formData.members.some((m) => m.id === member.id)
        && !formData.viewers.some((v) => v.id === member.id)),
    });
  };

  const handleSelectAllMembers = () => {
    setFormData({
      ...formData,
      members: workspaceMembers.filter((member) => !formData.managers.some((m) => m.id === member.id)
        && !formData.viewers.some((v) => v.id === member.id)),
    });
  };

  const handleSelectAllViewers = () => {
    setFormData({
      ...formData,
      viewers: workspaceMembers.filter((member) => !formData.managers.some((m) => m.id === member.id)
        && !formData.members.some((m) => m.id === member.id)),
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name || formData.name.trim().length === 0) {
      newErrors.name = 'Project name is required';
    } else if (formData.name.trim().length < 3) {
      newErrors.name = 'Project name must be at least 3 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      billable: true,
      disableActivity: false,
      disableIdleTime: false,
      client: '',
      managers: [],
      members: [],
      viewers: [],
      teams: [],
    });
    setErrors({});
    setSubmitError('');
    setActiveTab(0);
    onClose();
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setSubmitError('');

    try {
      const projectData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        billable: formData.billable,
        activity: !formData.disableActivity,
        idleTime: !formData.disableIdleTime,
        client: formData.client,
        members: {
          managers: formData.managers,
          members: formData.members,
          viewers: formData.viewers,
        },
        teams: formData.teams,
      };

      console.log('Sending project data:', projectData);

      await onSubmit(projectData);

      displayNotification({
        message: 'Project created successfully!',
        severity: 'success',
      });

      handleClose();
    } catch (error) {
      console.error('Error creating project:', error);
      setSubmitError(error.message || 'Failed to save project');
    }
  };

  useEffect(() => {
    if (open && activeWorkspace?.id) {
      fetchMembers();
      fetchTeams();
    }
  }, [open, activeWorkspace?.id]);

  const renderGeneralTab = () => (
    <Box sx={{
      display: 'flex', flexDirection: 'column', gap: 3, pt: 2,
    }}
    >
      {submitError && (
        <Alert severity="error">{submitError}</Alert>
      )}

      <Box>
        <Box sx={{
          mb: 1, fontSize: '14px !important', fontWeight: '500 !important', color: '#333 !important',
        }}
        >
          Project Name
        </Box>
        <TextField
          fullWidth
          placeholder="Required"
          value={formData.name}
          onChange={handleInputChange('name')}
          error={!!errors.name}
          helperText={errors.name}
          disabled={loading}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#f8f9fa',
              fontSize: '14px !important',
              '& fieldset': {
                borderColor: '#e0e0e0',
              },
              '& input': {
                fontSize: '14px !important',
              },
            },
          }}
        />
      </Box>

      <Box>
        <FormControlLabel
          control={(
            <Switch
              checked={formData.billable}
              onChange={handleInputChange('billable')}
              disabled={loading}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: '#4469A1 !important',
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: '#4469A1 !important',
                },
              }}
            />
          )}
          label="Billable"
          sx={{
            '& .MuiFormControlLabel-label': {
              fontSize: '14px !important',
              fontWeight: '500 !important',
              color: '#333 !important',
            },
          }}
        />
      </Box>

      <Box>
        <FormControlLabel
          control={(
            <Switch
              checked={formData.disableActivity}
              onChange={handleInputChange('disableActivity')}
              disabled={loading}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: '#4469A1 !important',
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: '#4469A1 !important',
                },
              }}
            />
          )}
          label="Disable Activity"
          sx={{
            '& .MuiFormControlLabel-label': {
              fontSize: '14px !important',
              fontWeight: '500 !important',
              color: '#333 !important',
            },
          }}
        />
      </Box>

      <Box>
        <FormControlLabel
          control={(
            <Switch
              checked={formData.disableIdleTime}
              onChange={handleInputChange('disableIdleTime')}
              disabled={loading}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: '#4469A1 !important',
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: '#4469A1 !important',
                },
              }}
            />
          )}
          label="Disable Idle Time"
          sx={{
            '& .MuiFormControlLabel-label': {
              fontSize: '14px !important',
              fontWeight: '500 !important',
              color: '#333 !important',
            },
          }}
        />
      </Box>

      <Box>
        <Box sx={{
          mb: 1, fontSize: '14px !important', fontWeight: '500 !important', color: '#333 !important',
        }}
        >
          Client
        </Box>
        <FormControl fullWidth>
          <Select
            value={formData.client}
            onChange={handleInputChange('client')}
            displayEmpty
            disabled={loading}
            sx={{
              backgroundColor: '#f8f9fa',
              fontSize: '14px !important',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#e0e0e0',
              },
              '& .MuiSelect-select': {
                fontSize: '14px !important',
              },
            }}
          >
            <MenuItem value="" sx={{ fontSize: '14px !important', color: '#999 !important' }}>
              Optional
            </MenuItem>
            <MenuItem value="client1" sx={{ fontSize: '14px !important' }}>Client 1</MenuItem>
            <MenuItem value="client2" sx={{ fontSize: '14px !important' }}>Client 2</MenuItem>
          </Select>
        </FormControl>
      </Box>
    </Box>
  );

  const renderMembersTab = () => (
    <Box sx={{
      display: 'flex', flexDirection: 'column', gap: 3, pt: 2,
    }}
    >
      <Box sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        padding: '12px',
        backgroundColor: '#f8f9ff !important',
        border: '1px solid #d6d9ff !important',
        borderRadius: '4px',
      }}
      >
        <Box>
          <img
          // eslint-disable-next-line camelcase
            src={error_outline}
            alt="Error Icon"
            width="16"
            height="16"
          />
        </Box>
        <Box sx={{ fontSize: '13px !important', color: '#4a5568 !important', lineHeight: '1.4 !important' }}>
          Moving Project viewers to Users or Managers will move them from a free seat to a paid seat,
          and may increase the number of paid seats on your plan.
        </Box>
      </Box>

      {isLoadingMembers ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
          <Box sx={{ ml: 2, fontSize: '14px', color: '#666' }}>Loading workspace members...</Box>
        </Box>
      ) : (
        <>
          <MemberSelector
            label="Managers"
            selectedMembers={formData.managers}
            availableMembers={workspaceMembers.filter((member) => !formData.members.some((m) => m.id === member.id)
              && !formData.viewers.some((v) => v.id === member.id))}
            onChange={(selectedMembers) => {
              setFormData((prev) => ({
                ...prev,
                managers: selectedMembers,
              }));
            }}
            onSelectAll={handleSelectAllManagers}
          />

          <MemberSelector
            label="Members"
            selectedMembers={formData.members}
            availableMembers={workspaceMembers
              .filter((member) => !formData.managers.some((m) => m.id === member.id)
              && !formData.viewers.some((v) => v.id === member.id))}
            onChange={(selectedMembers) => {
              setFormData((prev) => ({
                ...prev,
                members: selectedMembers,
              }));
            }}
            onSelectAll={handleSelectAllMembers}
          />

          <MemberSelector
            label="Viewers"
            selectedMembers={formData.viewers}
            availableMembers={workspaceMembers
              .filter((member) => !formData.managers.some((m) => m.id === member.id)
              && !formData.members.some((m) => m.id === member.id))}
            onChange={(selectedMembers) => {
              setFormData((prev) => ({
                ...prev,
                viewers: selectedMembers,
              }));
            }}
            onSelectAll={handleSelectAllViewers}
          />
        </>
      )}
    </Box>
  );

  const renderTeamsTab = () => (
    <Box sx={{
      display: 'flex', flexDirection: 'column', gap: 3, pt: 2,
    }}
    >
      {isLoadingTeams ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
          <Box sx={{ ml: 2, fontSize: '14px', color: '#666' }}>Loading workspace teams...</Box>
        </Box>
      ) : (
        <TeamSelector
          label="Teams"
          selectedTeams={formData.teams}
          availableTeams={workspaceTeams}
          onChange={(selectedTeams) => {
            setFormData((prev) => ({
              ...prev,
              teams: selectedTeams,
            }));
          }}
          onSelectAll={handleSelectAllTeams}
        />
      )}
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '8px',
          maxWidth: '520px',
        },
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: '18px !important',
        fontWeight: '600 !important',
        color: '#333 !important',
        pb: 1,
      }}
      >
        {title}
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: '14px !important',
              fontWeight: '500 !important',
              color: '#666 !important',
              minHeight: '40px',
              padding: '8px 16px',
            },
            '& .MuiTab-root.Mui-selected': {
              color: '#315285 !important',
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#315285',
            },
          }}
        >
          <Tab label="General" />
          <Tab label="Members" />
          <Tab label="Teams" />
        </Tabs>
      </Box>

      <DialogContent sx={{ px: 3, py: 0, minHeight: '300px' }}>
        {activeTab === 0 && renderGeneralTab()}
        {activeTab === 1 && renderMembersTab()}
        {activeTab === 2 && renderTeamsTab()}
      </DialogContent>

      <DialogActions sx={{
        px: 3, pb: 3, pt: 2, gap: 1,
      }}
      >
        <Button
          onClick={handleClose}
          disabled={loading}
          sx={{
            color: '#666 !important',
            fontSize: '14px !important',
            textTransform: 'none',
            padding: '8px 20px',
            border: '1px solid #e0e0e0',
            borderRadius: '4px',
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !formData.name.trim()}
          sx={{
            backgroundColor: loading || !formData.name.trim() ? '#a8d5a8 !important' : '#4caf50 !important',
            color: 'white !important',
            fontSize: '14px !important',
            textTransform: 'none',
            padding: '8px 24px',
            borderRadius: '4px',
            boxShadow: 'none !important',
            '&:hover': {
              backgroundColor: loading || !formData.name.trim() ? '#a8d5a8 !important' : '#45a049 !important',
              boxShadow: 'none !important',
            },
            '&.Mui-disabled': {
              backgroundColor: '#a8d5a8 !important',
              color: 'white !important',
              opacity: '1 !important',
            },
          }}
        >
          {loading ? (
            <>
              <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} />
              Saving...
            </>
          ) : (
            'Save'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

AddProjectModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  title: PropTypes.string,
  initialData: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    billable: PropTypes.bool,
    activity: PropTypes.bool,
    idleTime: PropTypes.bool,
    client: PropTypes.string,
    members: PropTypes.shape({
      managers: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string,
        email: PropTypes.string,
      })),
      members: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string,
        email: PropTypes.string,
      })),
      viewers: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string,
        email: PropTypes.string,
      })),
    }),
  }),
};

export default AddProjectModal;
